import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { startOfMonth, subMonths, format, startOfWeek, startOfQuarter, endOfWeek, endOfMonth, endOfQuarter } from "date-fns";

export interface PipelineMetrics {
  totalPipeline: number;
  avgDealSize: number;
  winRate: number;
  avgCycleDays: number;
  totalDeals: number;
  wonDeals: number;
  lostDeals: number;
}

export interface MonthlyPipelineData {
  month: string;
  value: number;
  deals: number;
}

export interface StageDistribution {
  stage: string;
  count: number;
  value: number;
  color: string;
}

export interface ConversionData {
  source: string;
  leads: number;
  converted: number;
  rate: number;
}

export interface ForecastData {
  period: string;
  committed: number;
  upside: number;
  pipeline: number;
}

export function usePipelineMetrics() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["pipeline-metrics", profile?.tenant_id],
    queryFn: async () => {
      // Get all deals
      const { data: deals, error } = await supabase
        .from("deals")
        .select("*, pipeline_stages(name, color, probability)");

      if (error) throw error;

      const openDeals = deals?.filter(d => d.status === "open") || [];
      const wonDeals = deals?.filter(d => d.status === "won") || [];
      const lostDeals = deals?.filter(d => d.status === "lost") || [];

      const totalPipeline = openDeals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
      const totalDealsCount = deals?.length || 0;
      const avgDealSize = totalDealsCount > 0 
        ? deals!.reduce((sum, d) => sum + (Number(d.amount) || 0), 0) / totalDealsCount 
        : 0;

      const completedDeals = wonDeals.length + lostDeals.length;
      const winRate = completedDeals > 0 ? (wonDeals.length / completedDeals) * 100 : 0;

      // Calculate average cycle in days for won deals
      let avgCycleDays = 0;
      if (wonDeals.length > 0) {
        const cycles = wonDeals.map(d => {
          const created = new Date(d.created_at);
          const updated = new Date(d.updated_at);
          return Math.round((updated.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
        });
        avgCycleDays = Math.round(cycles.reduce((a, b) => a + b, 0) / cycles.length);
      }

      return {
        totalPipeline,
        avgDealSize,
        winRate: Math.round(winRate),
        avgCycleDays: avgCycleDays || 28,
        totalDeals: totalDealsCount,
        wonDeals: wonDeals.length,
        lostDeals: lostDeals.length,
      } as PipelineMetrics;
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useMonthlyPipelineData() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["monthly-pipeline", profile?.tenant_id],
    queryFn: async () => {
      const { data: deals, error } = await supabase
        .from("deals")
        .select("amount, created_at, status");

      if (error) throw error;

      // Generate last 6 months
      const months: MonthlyPipelineData[] = [];
      for (let i = 5; i >= 0; i--) {
        const date = subMonths(new Date(), i);
        const monthStart = startOfMonth(date);
        const monthKey = format(date, "MMM");

        const monthDeals = deals?.filter(d => {
          const dealDate = new Date(d.created_at);
          return format(dealDate, "MMM yyyy") === format(monthStart, "MMM yyyy");
        }) || [];

        const value = monthDeals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

        months.push({
          month: monthKey,
          value,
          deals: monthDeals.length,
        });
      }

      return months;
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useStageDistribution() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["stage-distribution", profile?.tenant_id],
    queryFn: async () => {
      const { data: deals, error } = await supabase
        .from("deals")
        .select("amount, status, stage_id, pipeline_stages(name, color)")
        .eq("status", "open");

      if (error) throw error;

      // Group by stage
      const stageMap = new Map<string, StageDistribution>();
      
      deals?.forEach(deal => {
        const stageName = (deal.pipeline_stages as any)?.name || "Unknown";
        const stageColor = (deal.pipeline_stages as any)?.color || "#06B6D4";
        
        if (!stageMap.has(stageName)) {
          stageMap.set(stageName, {
            stage: stageName,
            count: 0,
            value: 0,
            color: stageColor,
          });
        }
        
        const stage = stageMap.get(stageName)!;
        stage.count++;
        stage.value += Number(deal.amount) || 0;
      });

      return Array.from(stageMap.values());
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useLeadConversionData() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["lead-conversion", profile?.tenant_id],
    queryFn: async () => {
      const { data: leads, error } = await supabase
        .from("leads")
        .select("source, status");

      if (error) throw error;

      // Group by source
      const sourceMap = new Map<string, { leads: number; converted: number }>();
      
      leads?.forEach(lead => {
        const source = lead.source || "manual";
        
        if (!sourceMap.has(source)) {
          sourceMap.set(source, { leads: 0, converted: 0 });
        }
        
        const data = sourceMap.get(source)!;
        data.leads++;
        if (lead.status === "converted") {
          data.converted++;
        }
      });

      return Array.from(sourceMap.entries()).map(([source, data]) => ({
        source: source.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
        leads: data.leads,
        converted: data.converted,
        rate: data.leads > 0 ? Math.round((data.converted / data.leads) * 100) : 0,
      })) as ConversionData[];
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useForecastData() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["forecast-data", profile?.tenant_id],
    queryFn: async () => {
      const now = new Date();
      const { data: deals, error } = await supabase
        .from("deals")
        .select("amount, expected_close_date, ai_win_probability, status, pipeline_stages(probability)")
        .eq("status", "open");

      if (error) throw error;

      const weekEnd = endOfWeek(now);
      const monthEnd = endOfMonth(now);
      const quarterEnd = endOfQuarter(now);

      const calculateForecast = (endDate: Date) => {
        const relevantDeals = deals?.filter(d => {
          if (!d.expected_close_date) return false;
          return new Date(d.expected_close_date) <= endDate;
        }) || [];

        let committed = 0;
        let upside = 0;
        let pipeline = 0;

        relevantDeals.forEach(deal => {
          const amount = Number(deal.amount) || 0;
          const probability = deal.ai_win_probability || (deal.pipeline_stages as any)?.probability || 50;

          if (probability >= 80) {
            committed += amount;
          } else if (probability >= 50) {
            upside += amount;
          } else {
            pipeline += amount;
          }
        });

        return { committed, upside, pipeline };
      };

      return [
        { period: "This Week", ...calculateForecast(weekEnd) },
        { period: "This Month", ...calculateForecast(monthEnd) },
        { period: "This Quarter", ...calculateForecast(quarterEnd) },
      ] as ForecastData[];
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useLeadStats() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["lead-stats", profile?.tenant_id],
    queryFn: async () => {
      const { data: leads, error } = await supabase
        .from("leads")
        .select("status, created_at");

      if (error) throw error;

      const total = leads?.length || 0;
      const newLeads = leads?.filter(l => l.status === "new").length || 0;
      const qualified = leads?.filter(l => l.status === "qualified").length || 0;
      const converted = leads?.filter(l => l.status === "converted").length || 0;
      const conversionRate = total > 0 ? Math.round((converted / total) * 100) : 0;

      return {
        total,
        newLeads,
        qualified,
        converted,
        conversionRate,
      };
    },
    enabled: !!profile?.tenant_id,
  });
}
